import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { BACKEND_URL } from '../app.config';
import { ListResult } from '../api/core/list-result';
import {
  CreateEmployerTaskDto,
  EmployerTask,
  EmployerTaskAttachmentUpload,
  EmployerTaskListFilter,
  TaskDetailsByAction,
  UpdateTaskStatusDto,
} from '../api/task/employer-task';

/** Upload endpoint returns BlobFileInfo (filePath), mapped to task attachment. */
interface BlobFileInfoResponse {
  fileName?: string;
  filePath?: string;
  fileSize?: number;
  mimeType?: string;
  mIMEType?: string;
}

@Injectable({
  providedIn: 'root',
})
export class TaskService {
  constructor(
    private readonly http: HttpClient,
    @Inject(BACKEND_URL) private readonly baseUrl: string,
  ) {}

  /** GET /api/app/task — paged employer task list. */
  getList(
    filter: EmployerTaskListFilter = {},
    sorting: string = '',
    skipCount: number = 0,
    maxResultCount: number = 10,
  ): Observable<ListResult<EmployerTask>> {
    let params = new HttpParams()
      .set('SkipCount', skipCount.toString())
      .set('MaxResultCount', maxResultCount.toString());

    if (sorting) {
      params = params.set('Sorting', sorting);
    }
    if (filter.matterId) {
      params = params.set('MatterId', filter.matterId);
    }
    if (filter.companyId) {
      params = params.set('CompanyId', filter.companyId);
    }
    if (filter.companyName) {
      params = params.set('CompanyName', filter.companyName);
    }
    if (filter.statuses?.length) {
      for (const status of filter.statuses) {
        params = params.append('Status', status.toString());
      }
    }

    return this.http.get<ListResult<EmployerTask>>(`${this.baseUrl}/api/app/task`, { params });
  }

  /** POST /api/app/task — create employer task. */
  createTask(payload: CreateEmployerTaskDto): Observable<EmployerTask> {
    return this.http.post<EmployerTask>(`${this.baseUrl}/api/app/task`, payload);
  }

  /** GET /api/app/task/{id} */
  getTask(id: string): Observable<EmployerTask> {
    return this.http.get<EmployerTask>(`${this.baseUrl}/api/app/task/${id}`);
  }

  /** GET /api/app/task/by-action/{actionId} — get employer task by workflow Create Task action id. */
  getByAction(actionId: string): Observable<EmployerTask> {
    return this.http.get<EmployerTask>(`${this.baseUrl}/api/app/task/by-action/${actionId}`);
  }

  /** GET /api/app/task/details-by-action-id?actionId= — task id + status for a Create Task action. */
  getDetailsByActionId(actionId: string): Observable<TaskDetailsByAction> {
    return this.http.get<TaskDetailsByAction>(`${this.baseUrl}/api/app/task/details-by-action-id`, {
      params: { actionId },
    });
  }

  /** POST /api/app/task/upload-attachment */
  uploadAttachment(file: File): Observable<EmployerTaskAttachmentUpload> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http
      .post<BlobFileInfoResponse>(`${this.baseUrl}/api/app/task/upload-attachment`, formData)
      .pipe(
        map((uploaded) => ({
          fileName: uploaded.fileName ?? file.name,
          sharePointFilePath: uploaded.filePath,
          fileSize: uploaded.fileSize ?? file.size,
          mimeType: uploaded.mimeType ?? uploaded.mIMEType ?? file.type,
        })),
      );
  }

  /** POST /api/app/task/update-status — Approved | Rejected | Reassigned | Cancelled */
  updateStatus(payload: UpdateTaskStatusDto): Observable<EmployerTask> {
    return this.http.post<EmployerTask>(`${this.baseUrl}/api/app/task/update-status`, payload);
  }
}
